import {
  initialConfig,
  LATEST_WEBCHAT_CONFIG_VERSION,
  migrateConfig,
  versionedConfigSchema,
  webchatConfigToWebchatIntegrationInput,
  type LatestVersionedWebchatConfig,
  type LatestWebchatConfig,
  type VersionedWebchatConfig,
} from '@bpinternal/shared'
import { showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import axios from 'axios'
import * as v from 'valibot'
import { create, type StoreApi, type UseBoundStore } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { trackEvent } from '~/providers/SegmentProvider'
import { queryClient } from '../../../providers/ReactQuery'
import { getQueryOptions } from '../../../services'
import { getApiClient } from '../../../stores'
import { getIntegrationsFromBot, updateBot } from '../../bots/services'

const normalizeDivId = (input: string | undefined): string | undefined => {
  if (typeof input === 'undefined') return undefined
  const normalizedInput = input.trim().replace(/ /g, '_').replace(/^#/, '')
  return normalizedInput
}

type WebchatConfigState = LatestVersionedWebchatConfig & {
  _hasHydrated: boolean
  _updating: boolean
  customStyleSheet?: string
  allowedOrigins?: string[]
  webhookId?: string
}

type WebchatConfigActions = {
  setBotName: (botName?: string) => void
  setBotAvatar: (botAvatar?: string) => void
  setBotDescription: (botDescription?: string) => void
  setFabImage: (imageUrl?: string) => void
  setComposerPlaceholder: (composerPlaceholder?: string) => void
  setFooter: (footer?: string) => void
  setAllowFileUpload: (allowFileUpload?: boolean) => void
  setEmail: (email?: { link: string; title: string }) => void
  setPhone: (phone?: { link: string; title: string }) => void
  setPrivacyPolicy: (privacyPolicy?: { link: string; title: string }) => void
  setTermsOfService: (termsOfService?: { link: string; title: string }) => void
  setWebsite: (website?: { link: string; title: string }) => void
  setColor: (color?: string) => void
  setThemeMode: (themeMode?: 'light' | 'dark') => void
  setEmbeddedChatId: (embeddedChatId?: string) => void
  setToggleChatId: (toggleChatId?: string) => void
  setVariant: (variant?: 'soft' | 'solid') => void
  setRadius: (radius?: number) => void
  setFontFamily: (fontFamily?: string) => void
  setCustomStyleSheet: (customStyleSheet?: string) => void
  setAllowedOrigins: (allowedOrigins?: string[]) => void
  setStorageLocation: (storageLocation?: LatestWebchatConfig['storageLocation']) => void
  setSoundEnabled: (soundEnabled?: boolean) => void
  setHeaderVariant: (headerVariant?: 'glass' | 'solid') => void
  setFeedbackEnabled: (feedbackEnabled?: boolean) => void
  setAdminSecret: (adminSecret?: string) => void
  setProactiveMessageEnabled: (proactiveMessageEnabled?: boolean) => void
  setProactiveBubbleMessage: (proactiveBubbleMessage?: string) => void
  setProactiveBubbleTriggerType: (proactiveBubbleTriggerType?: 'afterDelay' | 'userIdle') => void
  setProactiveBubbleDelayTime: (proactiveBubbleDelayTime?: number) => void
  saveConfig: () => Promise<void>
  _save: () => Promise<void>
  setHasHydrated: (hasHydrated: boolean) => void
}

const storeInstances: Record<string, UseBoundStore<StoreApi<WebchatConfigActions & WebchatConfigState>>> = {}

export const getUseConfigStore = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  const client = getApiClient({ botId, workspaceId })

  if (!storeInstances[botId]) {
    storeInstances[botId] = create<WebchatConfigActions & WebchatConfigState>()(
      persist(
        (set, get) => ({
          ...initialConfig,
          version: LATEST_WEBCHAT_CONFIG_VERSION,
          _hasHydrated: false,
          _updating: false,
          setAllowFileUpload: (allowFileUpload) => set({ allowFileUpload }),
          setBotAvatar: (botAvatar) => set({ botAvatar }),
          setBotDescription: (botDescription) => set({ botDescription }),
          setBotName: (botName) => set({ botName }),
          setFabImage: (imageUrl) => set({ fabImage: imageUrl }),
          setComposerPlaceholder: (composerPlaceholder) => set({ composerPlaceholder }),
          setEmail: (email) => set({ email }),
          setPhone: (phone) => set({ phone }),
          setPrivacyPolicy: (privacyPolicy) => set({ privacyPolicy }),
          setTermsOfService: (termsOfService) => set({ termsOfService }),
          setWebsite: (website) => set({ website }),
          setFooter: (footer) => set({ footer }),
          setColor: (color) => set({ color }),
          setThemeMode: (themeMode) => set({ themeMode }),
          setEmbeddedChatId: (embeddedChatId) => set({ embeddedChatId: normalizeDivId(embeddedChatId) }),
          setToggleChatId: (toggleChatId) => set({ toggleChatId: normalizeDivId(toggleChatId) }),
          setVariant: (variant) => set({ variant }),
          setRadius: (radius) => set({ radius }),
          setFontFamily: (fontFamily) => set({ fontFamily }),
          setCustomStyleSheet: (customStyleSheet) => set({ customStyleSheet }),
          setAllowedOrigins: (allowedOrigins) => set({ allowedOrigins }),
          setStorageLocation: (storageLocation) => set({ storageLocation }),
          setSoundEnabled: (soundEnabled) => set({ soundEnabled }),
          setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
          setHeaderVariant: (headerVariant) => set({ headerVariant }),
          setFeedbackEnabled: (feedbackEnabled) => set({ feedbackEnabled }),
          setAdminSecret: (adminSecret) => set({ adminSecret }),
          setProactiveMessageEnabled: (proactiveMessageEnabled) => set({ proactiveMessageEnabled }),
          setProactiveBubbleMessage: (proactiveBubbleMessage) => set({ proactiveBubbleMessage }),
          setProactiveBubbleTriggerType: (proactiveBubbleTriggerType) => set({ proactiveBubbleTriggerType }),
          setProactiveBubbleDelayTime: (proactiveBubbleDelayTime) => set({ proactiveBubbleDelayTime }),
          saveConfig: async () => {
            if (!get()._hasHydrated || get()._updating) {
              return
            }
            set({ _updating: true })
            try {
              await get()._save()
              showSuccessToast('Webchat configuration saved successfully')
            } catch (error) {
              showErrorToast('Failed to save webchat configuration')
            } finally {
              set({ _updating: false })
            }
          },
          _save: async () => {
            const currentState = get()

            const botQueryOptions = getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', {
              botId,
              workspaceId,
            })
            const bot = await queryClient.fetchQuery(botQueryOptions)
            const webchatIntegration = getIntegrationsFromBot(bot).find((bi) => bi.name === 'webchat')

            if (!webchatIntegration) {
              return
            }

            const { customStyleSheet, allowedOrigins, ...state } = currentState

            let additionalStylesheetUrl: string | undefined
            if (customStyleSheet) {
              const {
                file: { url },
              } = await getApiClient({ botId, workspaceId }).uploadFile({
                key: 'webchatAdditionalStyles.css',
                content: customStyleSheet,
                accessPolicies: ['public_content'],
                contentType: 'text/css',
                tags: {
                  webchatAdditionalStylesVersion: '1',
                  source: 'integration',
                  integrationName: 'webchat',
                  system: 'true',
                },
                publicContentImmediatelyAccessible: true,
              })
              additionalStylesheetUrl = url
            }
            const configResult = v.safeParse(webchatConfigToWebchatIntegrationInput, {
              ...state,
              additionalStylesheetUrl,
            })
            const configuration = configResult.success ? configResult.output : undefined
            await updateBot({
              workspaceId,
              id: botId,
              integrations: {
                [webchatIntegration.id]: {
                  enabled: webchatIntegration.enabled,
                  configuration: { ...configuration, allowedOrigins },
                },
              },
            })

            const mode: 'embedded' | 'custom_toggle' | 'fab_default' = configuration?.embeddedChatId
              ? 'embedded'
              : configuration?.toggleChatId
                ? 'custom_toggle'
                : 'fab_default'

            trackEvent({ type: 'webchat_config_saved', botId, workspaceId, mode })

            if (typeof configuration?.soundEnabled !== 'undefined') {
              trackEvent({
                type: 'chat_sound_toggle',
                enabled: configuration?.soundEnabled,
                botId,
                workspaceId,
              })
            }

            queryClient.invalidateQueries({
              queryKey: ['workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId }],
            })
          },
        }),
        {
          name: botId,
          onRehydrateStorage: (state) => () => state.setHasHydrated(true),
          storage: createJSONStorage(() => ({
            getItem: async () => {
              const files = await client.list
                .files({
                  tags: { webchatConfigVersion: 1, integrationName: 'webchat', source: 'integration' },
                })
                .collect()

              const [configFile] = files.sort(
                (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
              )

              if (!configFile) {
                return '{}'
              }

              const webchatConfig = await getWebchatConfig(configFile.url)
              const { additionalStylesheetUrl, ...state } = migrateConfig(webchatConfig)

              const botQueryOptions = getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', {
                botId,
                workspaceId,
              })
              const bot = await queryClient.fetchQuery(botQueryOptions)
              const webchatIntegration = getIntegrationsFromBot(bot).find((bi) => bi.name === 'webchat')

              const { allowedOrigins, adminSecret } = webchatIntegration?.configuration ?? {}
              const webhookId = webchatIntegration?.webhookId

              try {
                const { data: customStyleSheet } = additionalStylesheetUrl
                  ? await axios.get(additionalStylesheetUrl, {
                      withCredentials: false,
                    })
                  : { data: undefined }

                return JSON.stringify({
                  state: {
                    ...state,
                    ...(allowedOrigins ? { allowedOrigins } : {}),
                    ...(adminSecret ? { adminSecret } : {}),
                    customStyleSheet,
                    webhookId,
                  },
                })
              } catch (error) {
                return JSON.stringify({
                  state: {
                    ...state,
                    ...(allowedOrigins ? { allowedOrigins } : {}),
                    ...(adminSecret ? { adminSecret } : {}),
                  },
                })
              }
            },
            setItem: () => {
              // We don't want to auto save. We want to save only when the user clicks on save
            },
            removeItem: () => {
              // In our case we never want to delete the webchat config fite
            },
          })),
        }
      )
    )
  }
  return storeInstances[botId]
}

const getWebchatConfig = async (url: string): Promise<VersionedWebchatConfig> => {
  const responseVersionedConfigSchema = v.object({
    configuration: versionedConfigSchema,
  })

  const response = await fetch(url, {
    cache: 'no-store',
  })
  const data = v.safeParse(responseVersionedConfigSchema, await response.json())

  const webchatConfig = {
    ...initialConfig,
    ...(data?.success ? data.output.configuration : undefined),
  } as VersionedWebchatConfig

  return webchatConfig
}
